"use client"

import { useEffect, useRef, useTransition } from "react"
import { usePathname, useRouter } from "next/navigation"
import { canRefreshCatalog, isCatalogPage } from "@lib/vibe/catalog-refresh"

/** Refresh public catalogue data without navigating or touching cart/checkout flows. */
export default function CatalogAutoRefresh() {
  const pathname = usePathname()
  const router = useRouter()
  const [pending, startTransition] = useTransition()
  const lastRefresh = useRef(Date.now())
  const lastInteraction = useRef(0)

  useEffect(() => { lastRefresh.current = Date.now() }, [pathname])
  useEffect(() => {
    if (!isCatalogPage(pathname)) return
    const interacted = () => { lastInteraction.current = Date.now() }
    const refresh = () => {
      const editing = document.activeElement?.closest("input, textarea, select, [contenteditable=true]")
      const modal = document.querySelector('[role="dialog"], [data-headlessui-state~="open"], [aria-busy="true"]')
      const playing = Array.from(document.querySelectorAll("video")).some((video) => !video.paused && !video.ended)
      const now = Date.now()
      if (!canRefreshCatalog({ pathname, now, lastRefresh: lastRefresh.current,
        lastInteraction: lastInteraction.current, visible: document.visibilityState === "visible",
        online: navigator.onLine, busy: pending || !!editing || !!modal || playing })) return
      lastRefresh.current = now
      startTransition(() => router.refresh())
    }
    const timer = window.setInterval(refresh, 5_000)
    document.addEventListener("visibilitychange", refresh)
    window.addEventListener("online", refresh)
    window.addEventListener("focus", refresh)
    document.addEventListener("pointerdown", interacted, { passive: true })
    document.addEventListener("keydown", interacted)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener("visibilitychange", refresh)
      window.removeEventListener("online", refresh)
      window.removeEventListener("focus", refresh)
      document.removeEventListener("pointerdown", interacted)
      document.removeEventListener("keydown", interacted)
    }
  }, [pathname, router, pending])
  return null
}
